import {
  convertMeasure,
  getInputFieldTypes,
  InputFieldType,
  NullableMeasure,
  Product,
} from '@e2m-customer-portal/shared';
import { Direction } from 'domains/forecast/utils';
import { ForecastSlice } from '../../../../generated/graphql';

// Exposing private functions for granular testing
export const _test_moveProductionFocusUp = moveProductionFocusUp;
export const _test_moveProductionFocusDown = moveProductionFocusDown;
export const _test_moveProductionFocusLeft = moveProductionFocusLeft;
export const _test_moveProductionFocusRight = moveProductionFocusRight;

function moveProductionFocusUp(
  sourceRow: number,
  sourceColumn: number,
  lastRow: number,
  lastColumn: number,
  deviceMap: (HTMLInputElement | null)[][],
) {
  let row = sourceRow;
  let column = sourceColumn;

  do {
    if (row === 0 && column === 0) {
      column = lastColumn;
      row = lastRow;
    } else {
      column = row === 0 ? column - 1 : column;
      row = row === 0 ? lastRow : row - 1;
    }
  } while (deviceMap[row][column]?.disabled || deviceMap[row][column]?.readOnly || deviceMap[row][column] == null);

  return { row, column };
}

function moveProductionFocusDown(
  sourceRow: number,
  sourceColumn: number,
  lastRow: number,
  lastColumn: number,
  deviceMap: (HTMLInputElement | null)[][],
) {
  let row = sourceRow;
  let column = sourceColumn;

  do {
    if (row === lastRow && column === lastColumn) {
      column = 0;
      row = 0;
    } else {
      column = row === lastRow ? column + 1 : column;
      row = row === lastRow ? 0 : row + 1;
    }
  } while (deviceMap[row][column]?.disabled || deviceMap[row][column]?.readOnly || deviceMap[row][column] == null);

  return { row, column };
}

function moveProductionFocusLeft(
  sourceRow: number,
  sourceColumn: number,
  lastRow: number,
  lastColumn: number,
  deviceMap: (HTMLInputElement | null)[][],
) {
  let row = sourceRow;
  let column = sourceColumn;

  do {
    if (row === 0 && column === 0) {
      row = lastRow;
      column = lastColumn;
    } else {
      row = column === 0 ? row - 1 : row;
      column = column === 0 ? lastColumn : column - 1;
    }
  } while (deviceMap[row][column]?.disabled || deviceMap[row][column]?.readOnly || deviceMap[row][column] == null);

  return { row, column };
}

function moveProductionFocusRight(
  sourceRow: number,
  sourceColumn: number,
  lastRow: number,
  lastColumn: number,
  deviceMap: (HTMLInputElement | null)[][],
) {
  let row = sourceRow;
  let column = sourceColumn;

  do {
    if (row === lastRow && column === lastColumn) {
      row = 0;
      column = 0;
    } else {
      row = column === lastColumn ? row + 1 : row;
      column = column === lastColumn ? 0 : column + 1;
    }
  } while (deviceMap[row][column]?.disabled || deviceMap[row][column]?.readOnly || deviceMap[row][column] == null);

  return { row, column };
}

export function switchFocusForDeviceMap(deviceMap: Array<Array<HTMLInputElement | null>>) {
  return (sourceRow: number, sourceColumn: number, direction?: Direction) => {
    const { selectionStart, selectionEnd, value } = deviceMap[sourceRow][sourceColumn] ?? {
      selectionStart: null,
      selectionEnd: null,
      value: '',
    };
    const lastRow = deviceMap.length - 1;
    const lastColumn = deviceMap[0].length - 1;
    let row = sourceRow;
    let column = sourceColumn;

    switch (direction) {
      case Direction.Up:
        ({ row, column } = moveProductionFocusUp(sourceRow, sourceColumn, lastRow, lastColumn, deviceMap));
        break;

      case Direction.Down:
        ({ row, column } = moveProductionFocusDown(sourceRow, sourceColumn, lastRow, lastColumn, deviceMap));
        break;

      case Direction.Left:
        if (selectionStart !== 0) return false;
        ({ row, column } = moveProductionFocusLeft(sourceRow, sourceColumn, lastRow, lastColumn, deviceMap));
        break;

      case Direction.Right:
        if (selectionEnd !== value.length) return false;
        ({ row, column } = moveProductionFocusRight(sourceRow, sourceColumn, lastRow, lastColumn, deviceMap));
        break;
    }

    deviceMap[row][column]?.focus();
    return true;
  };
}

export function isKeyCombo(event: React.KeyboardEvent | KeyboardEvent, key?: string) {
  const isMatchingKey = key === undefined ? true : event.key === key;
  return (event.ctrlKey || event.metaKey) && isMatchingKey;
}

export function getCellWidthSum(
  row: number,
  startColumn: number,
  endColumn: number,
  fallbackCellWidth: number,
  deviceMap: Array<Array<HTMLInputElement | null>>,
) {
  // Columns with the same percentage-based widths can have different pixel widths,
  // so to keep the rectangle aligned with the table, we need to use the actual
  // cell widths of each column. The height is always the same.
  let width = 2;
  for (let index = Math.min(startColumn, endColumn); index <= Math.max(startColumn, endColumn); index++) {
    width += deviceMap[row][index] ? deviceMap[row][index]!.getBoundingClientRect().width + 1 : fallbackCellWidth;
  }
  return width;
}

export function rowFromDate(date: Date, isFifteenMinutesResolution?: boolean) {
  return isFifteenMinutesResolution ? date.getHours() * 4 + date.getMinutes() / 15 : date.getHours();
}

export function transformToControlEnergySlices(slices: readonly ForecastSlice[], products: readonly Product[]) {
  const transformedSlices = new Array<ForecastSlice>();

  let index = -1;
  let returnedSlice = slices[0];
  let lastTimezoneOffset = slices[0].date.getTimezoneOffset();
  let hasDowntime = false;
  const fcr = new Array<NullableMeasure>();
  const afrrNeg = new Array<NullableMeasure>();
  const afrrPos = new Array<NullableMeasure>();
  const mfrrNeg = new Array<NullableMeasure>();
  const mfrrPos = new Array<NullableMeasure>();
  for (const slice of slices) {
    index += 1;

    // Skip DST hour when switching to summer time
    if (slice.date.getTimezoneOffset() < lastTimezoneOffset) {
      index += 1;
    }

    lastTimezoneOffset = slice.date.getTimezoneOffset();

    // Collect all bands
    fcr.push(
      slice.fields.find((field) => field.type === InputFieldType.FCR)?.value
        ? slice.fields.find((field) => field.type === InputFieldType.FCR)!.value
        : { value: null, unit: 'W' },
    );
    afrrNeg.push(
      slice.fields.find((field) => field.type === InputFieldType.AFRR_NEGATIVE)?.value
        ? slice.fields.find((field) => field.type === InputFieldType.AFRR_NEGATIVE)!.value
        : { value: null, unit: 'W' },
    );
    afrrPos.push(
      slice.fields.find((field) => field.type === InputFieldType.AFRR_POSITIVE)?.value
        ? slice.fields.find((field) => field.type === InputFieldType.AFRR_POSITIVE)!.value
        : { value: null, unit: 'W' },
    );
    mfrrNeg.push(
      slice.fields.find((field) => field.type === InputFieldType.MFRR_NEGATIVE)?.value
        ? slice.fields.find((field) => field.type === InputFieldType.MFRR_NEGATIVE)!.value
        : { value: null, unit: 'W' },
    );
    mfrrPos.push(
      slice.fields.find((field) => field.type === InputFieldType.MFRR_POSITIVE)?.value
        ? slice.fields.find((field) => field.type === InputFieldType.MFRR_POSITIVE)!.value
        : { value: null, unit: 'W' },
    );

    if (slice.hasDowntime) hasDowntime = true;

    if (index % 4 === 0) returnedSlice = slice;
    if (index % 4 !== 3) continue;

    // Get the minimum of each band
    const minFCR = fcr.some((value) => value.value !== null)
      ? Math.min(...fcr.map((v) => convertMeasure(v, 'W').value!))
      : null;
    const minAFRRNeg = afrrNeg.some((value) => value.value !== null)
      ? Math.min(...afrrNeg.map((v) => convertMeasure(v, 'W').value!))
      : null;
    const minAFRRPos = afrrPos.some((value) => value.value !== null)
      ? Math.min(...afrrPos.map((v) => convertMeasure(v, 'W').value!))
      : null;
    const minMFRRNeg = mfrrNeg.some((value) => value.value !== null)
      ? Math.min(...mfrrNeg.map((v) => convertMeasure(v, 'W').value!))
      : null;
    const minMFRRPos = mfrrPos.some((value) => value.value !== null)
      ? Math.min(...mfrrPos.map((v) => convertMeasure(v, 'W').value!))
      : null;

    const fields = [
      {
        type: InputFieldType.FCR,
        value: { value: minFCR ?? 0, unit: 'W' },
        watt: minFCR,
      },
      {
        type: InputFieldType.AFRR_NEGATIVE,
        value: { value: minAFRRNeg ?? 0, unit: 'W' },
        watt: minAFRRNeg,
      },
      {
        type: InputFieldType.AFRR_POSITIVE,
        value: { value: minAFRRPos ?? 0, unit: 'W' },
        watt: minAFRRPos,
      },
      {
        type: InputFieldType.MFRR_NEGATIVE,
        value: { value: minMFRRNeg ?? 0, unit: 'W' },
        watt: minMFRRNeg,
      },
      {
        type: InputFieldType.MFRR_POSITIVE,
        value: { value: minMFRRPos ?? 0, unit: 'W' },
        watt: minMFRRPos,
      },
    ];

    transformedSlices.push({
      ...returnedSlice,
      // Only fill fields if at least one product has an actual value
      fields: [minFCR, minAFRRNeg, minAFRRPos, minMFRRNeg, minMFRRPos].some((value) => value !== null) ? fields : [],
      hasDowntime,
    });

    // Reset the collected minima
    fcr.length = 0;
    afrrNeg.length = 0;
    afrrPos.length = 0;
    mfrrNeg.length = 0;
    mfrrPos.length = 0;
    hasDowntime = false;
  }

  return transformedSlices.map((slice) => ({
    ...slice,
    fields: [...slice.fields].filter((field) => getInputFieldTypes(products, 'bands').includes(field.type)),
  }));
}

export function correctedSlicesAccordingToDST(
  allDeviceSlices: readonly ForecastSlice[],
  columns: number,
  isFifteenMinutesResolution?: boolean,
) {
  const summerIndex = allDeviceSlices.findIndex(
    (slice, index) => index - columns > 0 && slice.date.getDate() !== allDeviceSlices[index - columns].date.getDate(),
  );
  const winterIndex = allDeviceSlices.findIndex(
    (slice, index) =>
      index - 1 >= 0 &&
      allDeviceSlices[index - 1].date.getDate() === slice.date.getDate() &&
      allDeviceSlices[index - 1].date.getTimezoneOffset() !== slice.date.getTimezoneOffset(),
  );
  const dstIndex =
    Math.min(summerIndex, winterIndex) === -1 ? Math.max(summerIndex, winterIndex) : Math.min(summerIndex, winterIndex);

  const correctedSlices = allDeviceSlices.slice();

  // If isFifteenMinutesResolution is false, the loops stop after one pass.
  // Putting the check inside the head of the loop isn’t easier to understand,
  // but it makes Sonar happy.
  if (dstIndex !== -1 && dstIndex === winterIndex) {
    for (let dstSliceIndex = 0; dstSliceIndex < (isFifteenMinutesResolution ? 4 : 1); dstSliceIndex++) {
      correctedSlices.splice(winterIndex + dstSliceIndex * columns, 1);
    }
  } else if (dstIndex !== -1 && dstIndex === summerIndex) {
    for (let dstSliceIndex = 0; dstSliceIndex < (isFifteenMinutesResolution ? 4 : 1); dstSliceIndex++) {
      correctedSlices.splice(summerIndex + dstSliceIndex * columns, 0, {
        id: `buffer-slice-${dstSliceIndex}`,
        date: new Date(),
        fields: [],
        hasDowntime: false,
      } satisfies ForecastSlice);
    }
  }

  return correctedSlices;
}
